import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "hooks/useNotification";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  createTransportSedelWaste,
  TransportSedelQueryKey,
  WasteDto,
} from "api/transportSedel.api";
import { UppdragQueryKey } from "api/uppdrag.api";
import { Uppdrag } from "types/uppdrag.type";
import { useWasteCodeDropDown } from "api/wasteCode.api";
import { ComboBoxItem } from "components/ComboBox";
import { isAvskiljare } from "types/separator/separator.type";
import { getSeparatorDetailedLabel } from "types/separator/separator.helpers";

const wasteSchema = z.object({
  wasteCodeUuid: z.string({ message: "Obligatoriskt fält" }).uuid(),
  weightPreliminary: z.string().optional(),
  weightFinal: z.string().optional(),
  quantity: z.string().optional(),
  wasteCodeQuery: z.string().nullable().optional(),
  wasteType: z.enum(["Weight", "Quantity"]),
  tomningSeparatorUuid: z.string().uuid().nullable().optional(),
  tomningType: z
    .enum(["Heltomning", "Toppsugning", "NotApplicable", "NotAssigned"])
    .optional(),
  selectedWasteCode: z.custom<ComboBoxItem>(),
  comment: z.string().optional(),
});
export type TWasteSchema = z.infer<typeof wasteSchema>;

interface Input {
  transportSedel: { id: number };
  uppdrag: Uppdrag;
  separatorUuid?: string;
  setTomningSeparatorUuid?: Dispatch<SetStateAction<string | undefined>>;
  isSetSeparatorDisabled?: boolean;
  onCreateSuccessCb?: () => void;
  isSeparatorDropdownHidden?: boolean;
}

export const useCreateWasteLogic = ({
  transportSedel,
  uppdrag,
  separatorUuid,
  setTomningSeparatorUuid,
  isSetSeparatorDisabled,
  onCreateSuccessCb,
  isSeparatorDropdownHidden,
}: Input) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const form = useForm<TWasteSchema>({
    resolver: zodResolver(wasteSchema),
    defaultValues: {
      selectedWasteCode: {
        id: "",
        name: "",
        description: "",
      },
      wasteType: "Weight",
      tomningType: "Heltomning",
      wasteCodeQuery: "",
      tomningSeparatorUuid: separatorUuid, // Set default value directly
    },
  });

  useEffect(() => {
    if (isModalOpen) {
      // Reset the form but preserve the separator UUID value
      form.reset({
        selectedWasteCode: {
          id: "",
          name: "",
          description: "",
        },
        wasteType: "Weight",
        tomningType: "Heltomning",
        wasteCodeQuery: "",
        tomningSeparatorUuid: separatorUuid, // Preserve the separator UUID
      });
    }
  }, [form, isModalOpen, separatorUuid]);

  const onModalClose = () => {
    setIsModalOpen(false);
  };

  const afterModalClose = useCallback(() => {
    // Reset the form but keep the separator UUID if available
    form.reset({
      selectedWasteCode: {
        id: "",
        name: "",
        description: "",
      },
      wasteType: "Weight",
      tomningType: "Heltomning",
      wasteCodeQuery: "",
      tomningSeparatorUuid: separatorUuid, // Keep the separator UUID
    });

    // Only reset the external separator UUID if that's needed
    if (setTomningSeparatorUuid && !separatorUuid) {
      setTomningSeparatorUuid(undefined);
    }
  }, [form, setTomningSeparatorUuid, separatorUuid]);

  // CREATE
  const createMutation = useMutation({
    ...createTransportSedelWaste(transportSedel.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UppdragQueryKey.GetOne],
      });
      queryClient.invalidateQueries({
        queryKey: [TransportSedelQueryKey.GetList],
      });
      if (onCreateSuccessCb) {
        onCreateSuccessCb();
      }

      onModalClose();
    },
    onError: () => notification.error(),
  });

  const selectedSeparator = (uppdrag.facility.separatorList ?? []).find(
    (x) => x.uuid === (separatorUuid ?? form.watch("tomningSeparatorUuid")),
  );

  const isAvskiljareSelected =
    !!selectedSeparator && isAvskiljare(selectedSeparator);

  const onSubmit = form.handleSubmit((data: TWasteSchema) => {
    const relevantSeparatorUuid = separatorUuid ?? data.tomningSeparatorUuid; // Detta är så jävla dåligt men jag orkar inte mer
    const payload: WasteDto = {
      wasteCodeUuid: data.selectedWasteCode.id,
      weightPreliminary:
        data.wasteType === "Weight" ? data.weightPreliminary : undefined,
      weightFinal: data.wasteType === "Weight" ? data.weightFinal : undefined,
      quantity: data.wasteType === "Quantity" ? data.quantity : undefined,
      wasteType: data.wasteType,
      comment: data.comment,
      tomningSeparatorUuid: relevantSeparatorUuid,
      tomningType: isAvskiljareSelected ? data.tomningType : "NotApplicable",
    };

    createMutation.mutate(payload);
  });

  const separatorDropdownItems = (uppdrag.facility.separatorList ?? [])
    .map((x) => ({ label: getSeparatorDetailedLabel(x), value: x.uuid }))
    .sort((a, b) => (a.label < b.label ? -1 : 1));

  // waste codes
  const wasteCodeQuery = form.watch("wasteCodeQuery") ?? undefined;
  const selectedWasteCode = form.watch("selectedWasteCode");
  const { data: wasteCodes } = useWasteCodeDropDown(wasteCodeQuery);
  const wasteCodeComboBoxItems: ComboBoxItem[] = (wasteCodes ?? []).map(
    (c) => ({
      id: c.uuid,
      name: c.codePretty,
      description: c.description,
    }),
  );

  const setWasteCodeQuery = useCallback(
    (v?: string) => form.setValue("wasteCodeQuery", v),
    [form],
  );

  const setSelectedWasteCode = useCallback(
    (wc: ComboBoxItem) => {
      form.setValue(
        "selectedWasteCode",
        wc ?? {
          id: "",
          name: "",
          description: "",
        },
      );
      form.setValue("wasteCodeUuid", wc?.id ?? undefined);
    },
    [form],
  );

  return {
    isModalOpen,
    openModal: () => setIsModalOpen(true),
    form,
    onSubmit,
    onModalClose,
    afterModalClose,
    uppdrag,
    separatorDropdownItems,
    wasteCodeComboBoxItems,
    setWasteCodeQuery,
    selectedWasteCode,
    setSelectedWasteCode,
    isAvskiljareSelected,
    isPending: createMutation.isPending,
    hideSeparator: !!isSeparatorDropdownHidden,
    isSetSeparatorDisabled,
    selectedSeparator, // Expose the selected separator
  };
};

export type CreateWasteLogicRetVal = ReturnType<typeof useCreateWasteLogic>;
